/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import {
  useTheme,
  Typography,
  Checkbox,
  Select,
  ListItemText,
  MenuItem,
  InputLabel,
  Input,
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { useTranslation } from 'react-i18next';

import { ICustomTheme } from 'interfaces';
import { StyledCheckBoxIcon, StyledExpandMoreIcon } from './styles';

export type ItemType = {
  label: string;
  value: string;
};

type Props = {
  label: string;
  items: ItemType[];
  onChange: (filterList: string[]) => void;
  selectedItems: string[];
};

// Util function to format the expected prop -items-
export const formattedSelect = (
  list: { [key: string]: string }[],
  key: string,
): ItemType[] =>
  list.map((item: { [key: string]: string }) => ({
    value: item[key],
    label: item.label || item[key],
  }));

const MultiSelect = ({
  label,
  items,
  onChange,
  selectedItems,
}: Props): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onChange(event.target.value as string[]);
  };

  return (
    <React.Fragment>
      <InputLabel shrink css={{ marginBottom: theme.spacing(1) }}>
        {label}
      </InputLabel>
      <Select
        displayEmpty
        multiple
        placeholder={t('challenges.declaration.all')}
        value={selectedItems}
        onChange={handleChange}
        input={
          <Input
            css={{ verticalAlign: 'middle', color: theme.colors.grey.dark }}
          />
        }
        renderValue={(selected) => {
          if (!(selected as string[]).length) {
            return (
              <Typography
                css={{ marginLeft: theme.spacing(1) }}
                variant="body1"
                align="left"
              >
                {t('challenges.declaration.all')}
              </Typography>
            );
          }
          return (
            <Typography variant="body1" align="center">
              {(selected as string[]).length}{' '}
              {t('challenges.declaration.selected')}
            </Typography>
          );
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          PaperProps: {
            style: {
              maxHeight: 300,
              padding: theme.spacing(1),
              marginTop: theme.spacing(2),
            },
          },
        }}
        disableUnderline
        IconComponent={StyledExpandMoreIcon}
        css={{
          width: '100%',
          boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
          marginBottom: theme.spacing(2),
        }}
      >
        {items.map((item: ItemType) => (
          <MenuItem
            key={item.value}
            value={item.value}
            css={{
              borderBottom: `1px solid ${theme.colors.grey.light3}`,
            }}
          >
            <Checkbox
              color="primary"
              checkedIcon={<StyledCheckBoxIcon fontSize="large" />}
              icon={<CheckBoxOutlineBlankIcon fontSize="large" />}
              checked={selectedItems.includes(item.value)}
              css={{ fontSize: theme.typography.h3.fontSize }}
            />
            <ListItemText
              primary={item.label}
              css={{
                fontSize: theme.typography.body2.fontSize,
                ...(!item.value && { fontStyle: 'italic' }),
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </React.Fragment>
  );
};

export default MultiSelect;
